import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminCronRuns, useTriggerCronRun } from '@/hooks/useAdmin';
import { Pill } from '@/components/ui/pill';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { CronRun, CronRunStatus } from '@/api/types';

/**
 * What each job does, in words, so "Run now" is never a guess. Jobs that move
 * money say so and require a confirm.
 */
const JOB_INFO: Record<string, { label: string; detail: string; movesMoney: boolean }> = {
  'outreach-morning': {
    label: 'Morning check',
    detail: 'Re-checks every tracked invite with clipping (joins, posts, followers) and updates what each is worth.',
    movesMoney: false,
  },
  'release-expired-invites': {
    label: 'Expire old invites',
    detail: "Expires invites past their team's re-log window that never joined, so anyone can log the creator again.",
    movesMoney: false,
  },
  'sync-clipping-payouts': {
    label: 'Sync payouts',
    detail: 'Asks the payout provider which payouts were paid and records them. Changes what outreachers have been paid.',
    movesMoney: true,
  },
  'award-challenge-prizes': {
    label: 'Award challenge prizes',
    detail: 'Pays the prize for every ended challenge to its current leader. Sends real money.',
    movesMoney: true,
  },
};

/**
 * Header shortcut: shows the last cron run (and a failure flag) at a glance,
 * and opens Setup → System, where the run history lives.
 */
export function CronRunsButton(): JSX.Element {
  const { data, isLoading, isError } = useAdminCronRuns();
  const groups = data?.groups ?? [];
  const latestOverall = pickLatestRun(groups);
  const anyFailedRecently = groups.some((g) => g.runs.slice(0, 5).some((r) => r.status === 'FAILED'));
  return (
    <Button
      asChild
      variant="outline"
      size="sm"
      className={anyFailedRecently ? 'border-destructive/50 text-destructive' : undefined}
    >
      <Link to="/admin/system?tab=jobs" title="Scheduled jobs and their run history">
        {anyFailedRecently ? <AlertCircle className="mr-1.5 h-3.5 w-3.5" /> : <Activity className="mr-1.5 h-3.5 w-3.5" />}
        {isLoading
          ? 'Scheduled jobs'
          : isError
            ? 'Scheduled jobs · failed to load'
            : !latestOverall
              ? 'Scheduled jobs · no runs yet'
              : `${anyFailedRecently ? 'Recent job failure · ' : 'Last run '}${formatDistanceToNow(new Date(latestOverall.startedAt), { addSuffix: true })}`}
      </Link>
    </Button>
  );
}

/** Run history for every scheduled job. Lives on Setup → System. */
export function CronRunsPanel(): JSX.Element {
  const { data, isLoading, isError } = useAdminCronRuns();
  const groups = data?.groups ?? [];
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading run history…</p>;
  if (isError) return <p className="text-sm text-destructive">Couldn't load run history. Reload the page to try again.</p>;
  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No runs recorded yet. History starts from when run recording was switched on; older runs aren't backfilled.
      </p>
    );
  }
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <JobSection key={g.jobName} jobName={g.jobName} runs={g.runs} />
      ))}
    </div>
  );
}

function JobSection({ jobName, runs }: { jobName: string; runs: CronRun[] }): JSX.Element {
  const trigger = useTriggerCronRun();
  // Pending state is keyed by the variables of the in-flight mutation so
  // clicking "Run now" on outreach-morning doesn't spin the sync-clipping-
  // payouts button too.
  const isTriggering = trigger.isPending && trigger.variables === jobName;

  const info = JOB_INFO[jobName];

  function handleRun(): void {
    trigger.mutate(jobName, {
      onSuccess: () => {
        toast.success(`${info?.label ?? jobName} started. The result appears here when it finishes.`);
      },
      onError: (err: Error) => {
        toast.error(err.message);
      },
    });
  }

  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-medium">
            {info?.label ?? jobName}
            {info?.movesMoney && (
              <span className="ml-2 rounded-sm bg-warning/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-pending-strong">
                Moves money
              </span>
            )}
          </h3>
          {info && <p className="mt-0.5 max-w-prose text-xs text-muted-foreground">{info.detail}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            last {runs.length} run{runs.length === 1 ? '' : 's'}
          </span>
          <ConfirmDialog
            trigger={
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isTriggering}
                aria-label={`Run ${info?.label ?? jobName} now`}
                className="h-7 gap-1 text-xs"
              >
                {isTriggering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Run now
              </Button>
            }
            title={`Run "${info?.label ?? jobName}" now?`}
            description={
              <>
                {info?.detail ?? 'Runs this job immediately, outside its schedule.'}
                {info?.movesMoney && <strong className="mt-2 block">This moves real money and can't be undone from here.</strong>}
              </>
            }
            tone={info?.movesMoney ? 'destructive' : 'default'}
            confirmLabel="Run now"
            pending={isTriggering}
            onConfirm={handleRun}
          />
        </div>
      </div>
      {runs.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No runs recorded.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {runs.map((r) => (
            <RunRow key={r.id} run={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RunRow({ run }: { run: CronRun }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hasDetail =
    run.status === 'FAILED' ||
    (run.summary !== null && run.summary !== undefined && Object.keys(run.summary).length > 0);

  return (
    <li>
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((v) => !v)}
        disabled={!hasDetail}
        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs ${
          hasDetail ? 'hover:bg-muted/40' : 'cursor-default'
        }`}
        aria-expanded={hasDetail ? expanded : undefined}
      >
        <div className="flex items-center gap-2">
          {hasDetail ? (
            expanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )
          ) : (
            <span className="h-3 w-3" aria-hidden />
          )}
          <StatusGlyph status={run.status} />
          <span className="font-mono text-muted-foreground">
            {new Date(run.startedAt).toLocaleString()}
          </span>
          <Pill tone={run.status === 'OK' ? 'paid' : 'failed'}>{run.status}</Pill>
          {run.trigger === 'MANUAL' && (
            <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Manual
            </span>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Clock className="h-3 w-3" />
          {formatDuration(run.durationMs)}
        </span>
      </button>

      {expanded && hasDetail && (
        <div className="border-t bg-muted/20 px-3 py-2 text-xs">
          {run.status === 'FAILED' && run.errorMessage && (
            <div className="mb-2">
              <p className="mb-1 font-medium text-destructive">Error</p>
              <pre className="whitespace-pre-wrap break-words text-destructive">
                {run.errorMessage}
              </pre>
            </div>
          )}
          {run.summary && (
            <div>
              <p className="mb-1 font-medium text-muted-foreground">Summary</p>
              <pre className="whitespace-pre-wrap break-words font-mono text-muted-foreground">
                {JSON.stringify(run.summary, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function StatusGlyph({ status }: { status: CronRunStatus }): JSX.Element {
  if (status === 'OK') {
    return <CheckCircle2 className="h-3.5 w-3.5 text-success-strong" aria-label="ok" />;
  }
  return <AlertCircle className="h-3.5 w-3.5 text-destructive" aria-label="failed" />;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function pickLatestRun(groups: { runs: CronRun[] }[]): CronRun | null {
  let latest: CronRun | null = null;
  for (const g of groups) {
    const first = g.runs[0];
    if (!first) continue;
    if (!latest || new Date(first.startedAt) > new Date(latest.startedAt)) {
      latest = first;
    }
  }
  return latest;
}
